import Clarity from '@microsoft/clarity';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePlatform } from 'hooks';
import type { ConsentType, UserConsents } from 'types/consent';
import { environment } from 'utils/environmentVariables';
import { ErrorResponse } from './fetchAPI';
import { useFetchApi } from './useFetchApi';

const queryKeys = {
  all: () => ['consent'] as const,
  getConsents: (isWeb: boolean) =>
    [...queryKeys.all(), 'getConsent', isWeb] as const,
  saveConsents: () => [...queryKeys.all(), 'saveConsent'] as const,
};

export const useGetConsents = (enabled: boolean = true) => {
  const { fetch } = useFetchApi();
  const { isWeb } = usePlatform();
  return useQuery<
    { consents: ConsentType[]; userConsents: UserConsents },
    ErrorResponse
  >({
    queryKey: queryKeys.getConsents(isWeb),
    queryFn: async () => {
      const { consents } = await fetch<{
        consents: ConsentType[];
      }>('kyc/consents');
      const userConsents: UserConsents = consents.reduce(
        (acc, curr) => ({
          ...acc,
          ...(!curr.isObligatory ? { [curr.id]: curr.isConsenting } : {}),
        }),
        {}
      );
      // https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-consent
      // Clarity requires consent for data analysis
      // Clarity.consent(false) will disable all tracking
      if (isWeb && environment.REACT_APP_CLARITY_ID) {
        Clarity.consent(userConsents['DATA_ANALYSIS']);
      }
      return { consents, userConsents };
    },
    staleTime: Infinity,
    enabled,
  });
};

export const useUpdateConsents = () => {
  const { fetch } = useFetchApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: queryKeys.saveConsents(),
    mutationFn: (consentsToUpdate: UserConsents) => {
      return fetch('kyc/consents', {
        method: 'PATCH',
        body: { consentsToUpdate },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.all() });
    },
  });
};
